import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  Link,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Gavel,
  Policy,
  Warning,
  Lock,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { logoutUser } from '../../store/slices/authSlice';
import { useTranslation } from 'react-i18next';

interface AgreementModalProps {
  open: boolean;
  userId: string;
  onAccept?: () => void;
}

const CURRENT_AGREEMENT_VERSION = '01-08-2025';

const AgreementModal: React.FC<AgreementModalProps> = ({ 
  open, 
  userId,
  onAccept 
}) => {
  const [clubAgreementAccepted, setClubAgreementAccepted] = useState(false);
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const handleAccept = async () => {
    if (!clubAgreementAccepted || !privacyPolicyAccepted) {
      setError(t('agreements.bothRequired'));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await api.post(`/users/${userId}/accept-agreements`, {
        club_agreement: clubAgreementAccepted,
        club_agreement_version: CURRENT_AGREEMENT_VERSION,
        privacy_policy: privacyPolicyAccepted,
        privacy_policy_version: CURRENT_AGREEMENT_VERSION,
      });

      if (onAccept) {
        onAccept();
      }
    } catch (err: any) {
      console.error('Failed to accept agreements:', err);
      setError('Failed to save your agreement acceptance. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate('/');
  };

  return (
    <Dialog 
      open={open} 
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown
      onClose={(event, reason) => {
        // Prevent closing by clicking outside
        if (reason === 'backdropClick') {
          return;
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: 'warning.main', 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <Lock />
        {t('agreements.title')}
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 3 }}>
          <Typography variant="body2">
            {t('agreements.subtitle')}
          </Typography>
        </Alert>

        <Typography variant="body1" sx={{ mb: 3 }}>
          {t('agreements.subtitle')}
        </Typography>

        <Box sx={{ mb: 3 }}>
          {/* Terms and Conditions / Club Agreement */}
          <Box sx={{ 
            p: 2, 
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            mb: 2,
            bgcolor: 'background.paper'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Gavel color="primary" />
              <Typography variant="subtitle1" fontWeight="600">
                {t('agreements.termsTitle')}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('agreements.lastUpdated')}: {CURRENT_AGREEMENT_VERSION}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={clubAgreementAccepted}
                  onChange={(e) => setClubAgreementAccepted(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  {t('agreements.termsCheckbox')}{' '}
                  <Link
                    href="https://www.hikeandfly.app/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </Link>
                  {' and '}
                  <Link
                    href="https://www.hikeandfly.app/club-agreement"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Club Operating Agreement
                  </Link>
                  {' '}(Version: {CURRENT_AGREEMENT_VERSION})
                </Typography>
              }
            />
          </Box>

          {/* Privacy Policy */}
          <Box sx={{ 
            p: 2, 
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Policy color="primary" />
              <Typography variant="subtitle1" fontWeight="600">
                {t('agreements.privacyTitle')}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('agreements.lastUpdated')}: {CURRENT_AGREEMENT_VERSION}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={privacyPolicyAccepted}
                  onChange={(e) => setPrivacyPolicyAccepted(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  {t('agreements.privacyCheckbox')}{' '}
                  <Link
                    href="https://www.hikeandfly.app/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </Link>
                  {' '}and how my data will be processed (Version: {CURRENT_AGREEMENT_VERSION})
                </Typography>
              }
            />
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="caption" color="text.secondary">
          {t('agreements.bothRequired')}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={handleLogout}
          color="inherit"
          disabled={loading}
        >
          {t('menu.logout')}
        </Button>
        <Button
          onClick={handleAccept}
          variant="contained"
          disabled={loading || !clubAgreementAccepted || !privacyPolicyAccepted}
          sx={{
            minWidth: 120,
            background: 'linear-gradient(45deg, #3388ff 30%, #66a3ff 90%)',
            '&:disabled': {
              background: 'none',
              bgcolor: 'action.disabledBackground',
            },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : t('agreements.acceptButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgreementModal;